import type { AnnotationDto, ElementDetail } from "../../../../types/bim-viewer.types";

/**
 * Lightweight search index — ~50 representative elements covering all disciplines.
 * Used by Track 2's ⌘K command palette before the backend search service ships.
 */
export interface ElementSearchEntry {
  elementId: number;
  expressId: number;
  globalId: string;
  name: string;
  ifcClass: string;
  level: string;
  discipline: string;
  /** Free-text bag of property values for fuzzy matching (size, material, system). */
  keywords: string[];
}

const lvl = (n: number) =>
  n === 0 ? "Ground Floor" : n < 0 ? `Basement ${Math.abs(n)}` : `Level ${n}`;

let nextId = 100001;
const id = () => nextId++;

const guid = (i: number) => `1A2B${i.toString(36).padStart(8, "0").toUpperCase()}XYZ7Q4`;

export const mockSearchIndex: ElementSearchEntry[] = [
  // Walls
  ...Array.from({ length: 8 }, (_, i) => ({
    elementId: id(),
    expressId: 12000 + i,
    globalId: guid(i),
    name: `External Wall — Type A ${i + 1}`,
    ifcClass: "IfcWall",
    level: lvl(i % 6),
    discipline: "ARCHITECTURAL",
    keywords: ["concrete", "200mm", "external", "load-bearing"],
  })),
  // Columns
  ...Array.from({ length: 6 }, (_, i) => ({
    elementId: id(),
    expressId: 13000 + i,
    globalId: guid(100 + i),
    name: `RC Column C-${(i + 1).toString().padStart(3, "0")}`,
    ifcClass: "IfcColumn",
    level: lvl(i),
    discipline: "STRUCTURAL",
    keywords: ["concrete", "C40", "600x600", "load-bearing", "rebar"],
  })),
  // Beams
  ...Array.from({ length: 6 }, (_, i) => ({
    elementId: id(),
    expressId: 14000 + i,
    globalId: guid(200 + i),
    name: `Primary Beam B-${(i + 1).toString().padStart(3, "0")}`,
    ifcClass: "IfcBeam",
    level: lvl(i + 1),
    discipline: "STRUCTURAL",
    keywords: ["steel", "I-section", "UB 533x210", "primary"],
  })),
  // Doors / Windows
  ...Array.from({ length: 4 }, (_, i) => ({
    elementId: id(),
    expressId: 15000 + i,
    globalId: guid(300 + i),
    name: `Door D-${i + 1}`,
    ifcClass: "IfcDoor",
    level: lvl(i),
    discipline: "ARCHITECTURAL",
    keywords: ["timber", "single-leaf", "fire-rated", "FD60"],
  })),
  ...Array.from({ length: 4 }, (_, i) => ({
    elementId: id(),
    expressId: 16000 + i,
    globalId: guid(400 + i),
    name: `Window W-${i + 1}`,
    ifcClass: "IfcWindow",
    level: lvl(i + 1),
    discipline: "ARCHITECTURAL",
    keywords: ["aluminium", "double-glazed", "1200x1500"],
  })),
  // MEP — pipes
  ...Array.from({ length: 8 }, (_, i) => ({
    elementId: id(),
    expressId: 17000 + i,
    globalId: guid(500 + i),
    name: `CHW Pipe ${200 - i * 10}mm — Run ${i + 1}`,
    ifcClass: "IfcPipeSegment",
    level: lvl(5),
    discipline: "MEP_MECHANICAL",
    keywords: ["chilled water", "200mm", "150mm", "insulated", "supply"],
  })),
  // MEP — ducts
  ...Array.from({ length: 6 }, (_, i) => ({
    elementId: id(),
    expressId: 18000 + i,
    globalId: guid(600 + i),
    name: `Supply Duct — Zone ${i + 1}`,
    ifcClass: "IfcDuctSegment",
    level: lvl(4),
    discipline: "MEP_MECHANICAL",
    keywords: ["HVAC", "supply", "rectangular", "600x400"],
  })),
  // MEP — electrical
  ...Array.from({ length: 4 }, (_, i) => ({
    elementId: id(),
    expressId: 19000 + i,
    globalId: guid(700 + i),
    name: `Light Fixture LF-${i + 1}`,
    ifcClass: "IfcLightFixture",
    level: lvl(2),
    discipline: "MEP_ELECTRICAL",
    keywords: ["LED", "recessed", "office", "4000K"],
  })),
  // Plumbing
  ...Array.from({ length: 4 }, (_, i) => ({
    elementId: id(),
    expressId: 20000 + i,
    globalId: guid(800 + i),
    name: `WC Basin WB-${i + 1}`,
    ifcClass: "IfcSanitaryTerminal",
    level: lvl(1),
    discipline: "MEP_PLUMBING",
    keywords: ["ceramic", "wall-hung", "low-flow"],
  })),
];

/**
 * Detailed Psets for the first element — used by RightSidebar v2 once it lands.
 */
export const mockElementDetail: ElementDetail = {
  elementId: mockSearchIndex[0].elementId,
  expressId: mockSearchIndex[0].expressId,
  globalId: mockSearchIndex[0].globalId,
  ifcClass: "IfcWall",
  elementName: mockSearchIndex[0].name,
  elementType: "External Wall Type A",
  storeyName: "Ground Floor",
  storeyIndex: 0,
  buildingName: "Tower A",
  description: "External load-bearing wall, 200mm RC with insulation and rainscreen.",
  isExternal: true,
  isLoadbearing: true,
  propertySets: [
    {
      psetName: "Pset_WallCommon",
      properties: [
        { propertyName: "IsExternal", propertyValue: "True", propertyType: "Boolean" },
        { propertyName: "LoadBearing", propertyValue: "True", propertyType: "Boolean" },
        { propertyName: "FireRating", propertyValue: "EI120", propertyType: "Label" },
        { propertyName: "AcousticRating", propertyValue: "Rw 52 dB", propertyType: "Label" },
        { propertyName: "ThermalTransmittance", propertyValue: "0.22", propertyType: "Real (W/m²K)" },
      ],
    },
    {
      psetName: "Pset_QuantityTakeOff",
      properties: [
        { propertyName: "Length", propertyValue: "6.40", propertyType: "Real (m)" },
        { propertyName: "Height", propertyValue: "3.20", propertyType: "Real (m)" },
        { propertyName: "GrossArea", propertyValue: "20.48", propertyType: "Real (m²)" },
        { propertyName: "NetArea", propertyValue: "17.92", propertyType: "Real (m²)" },
        { propertyName: "Volume", propertyValue: "3.58", propertyType: "Real (m³)" },
      ],
    },
    {
      psetName: "ProcureX_BOQ",
      properties: [
        { propertyName: "BoqCode", propertyValue: "A.01.04", propertyType: "Label" },
        { propertyName: "BoqDescription", propertyValue: "RC external wall 200mm, C40 finish", propertyType: "Label" },
        { propertyName: "UnitRate", propertyValue: "1850.00", propertyType: "Real (INR/m²)" },
        { propertyName: "CostCentre", propertyValue: "TWR-A-SHELL", propertyType: "Label" },
      ],
    },
  ],
  materials: [
    { materialName: "Concrete C40", materialCategory: "Structural", layerThickness: 0.2, layerIndex: 0 },
    { materialName: "Mineral Wool", materialCategory: "Insulation", layerThickness: 0.1, layerIndex: 1 },
    { materialName: "Aluminium Rainscreen", materialCategory: "Cladding", layerThickness: 0.02, layerIndex: 2 },
  ],
};

export const mockAnnotations: AnnotationDto[] = [
  {
    id: 50001,
    modelId: 9001,
    projectId: 42,
    elementId: mockSearchIndex[0].elementId,
    title: "Confirm fire rating",
    description: "Consultant to verify EI120 rating; current submittal is EI90.",
    position: { x: 12.4, y: 3.1, z: 0.0 },
    cameraPosition: { eye: [25, 12, 15], target: [12.4, 3.1, 0] },
    annotationType: "issue",
    status: "open",
    createdBy: 7,
    createdAt: "2026-05-22T10:08:00Z",
  },
];
